// iOS native-file-picker helpers.
//
// Why this exists: a web page cannot detect the moment the user taps the iOS
// photo picker's blue "Validate / Add" button — there is no event for it. The
// only signal a web `<input type="file">` gives us is the `change` event, and
// iOS does NOT fire it until it has finished downloading every selected photo
// from iCloud (and transcoding any HEIC, when the accept list forces it). For a
// large multi-select that download window is many seconds, during which the
// page looks frozen and the customer assumes the app is broken and leaves.
//
// The mitigation (see usages in the customer upload pages) is to show a
// "Chargement de vos photos…" state from the moment we OPEN the picker — i.e.
// before `input.click()` — and keep it until files arrive, the picker is
// cancelled, or a safety timeout fires. That feedback is only meaningful on
// iOS; desktop pickers return synchronously, so we gate it on this detector.

/**
 * Pure user-agent test for an iOS device (iPhone / iPad / iPod). Kept pure and
 * argument-driven so it is unit-testable without a DOM. Mirrors the inline
 * `/iPad|iPhone|iPod/` check AlbumFlowPage already uses, so all upload paths
 * agree on what counts as iOS.
 *
 * Note: iPadOS 13+ Safari reports a desktop "Macintosh" UA by default, so this
 * intentionally does NOT try to catch that case — matching the existing
 * AlbumFlowPage behaviour. Our customer base is overwhelmingly iPhone.
 */
export function isIosUserAgent(ua: string): boolean {
  return /iPad|iPhone|iPod/.test(ua);
}

/**
 * Runtime convenience wrapper: true when the current device is iOS. The
 * `MSStream` guard excludes old Windows Phones that spoofed an iPhone UA.
 * Safe to call during SSR / tests (returns false when there is no navigator).
 */
export function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const hasMSStream = !!(window as unknown as { MSStream?: unknown }).MSStream;
  return isIosUserAgent(navigator.userAgent) && !hasMSStream;
}

/**
 * Explicit still-image list (incl. HEIC by MIME *and* extension) used on iOS.
 * Naming HEIC explicitly stops iOS Safari from transcoding every selected HEIC
 * to JPEG *before* it closes the picker (~3 s/photo → a ~60 s freeze on a
 * 20-photo batch); iOS then hands the originals through instantly and our own
 * in-app overlay does the conversion.
 */
export const IOS_PHOTO_ACCEPT =
  'image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif';

/**
 * The `accept` value for a bulk-photo `<input type="file">` that reliably opens
 * the GALLERY / Google Photos with multi-select on EVERY device — never the
 * camera. Pair it with `multiple` and NO `capture` attribute.
 *
 * Device-aware because the two platforms need opposite things:
 *  - **Android Chrome**: an explicit multi-MIME list containing HEIC types and
 *    bare `.heic/.heif` extensions makes Chrome resolve the file intent to a
 *    generic chooser that offers Camera + Files but HIDES the gallery (the
 *    reported camera-only bug). A bare `image/*` lets Google Photos/Gallery
 *    claim the intent, so it shows first with multi-select. Android photos are
 *    overwhelmingly JPEG, and `image/*` still surfaces any HEIF the device has.
 *  - **iOS Safari**: `image/*` triggers the slow pre-transcode above, so iOS
 *    gets the explicit {@link IOS_PHOTO_ACCEPT} list instead.
 *
 * Desktop is grouped with Android (`image/*`) — desktop pickers honour it and
 * have no camera intent to worry about.
 */
export function galleryAccept(): string {
  return isIosDevice() ? IOS_PHOTO_ACCEPT : 'image/*';
}
